import { isJwtRejected } from "@/app/plugin-status/workerState";
import { PluginStatus } from "@/services/pluginStatusApi";
import { QueueSummaryResponse, QueueSummarySection } from "@/services/workerApi";

export type WorkerTabKey = "overview" | "distribution" | "operations";

export interface WorkerAlert {
  id: string;
  severity: "warning" | "danger";
  title: string;
  detail: string;
  tab?: WorkerTabKey;
}

// Seuils heuristiques de détection — volontairement centralisés et commentés
// car à calibrer avec l'équipe (faux positifs possibles en backfill massif).
export const ALERT_THRESHOLDS = {
  famineEnrichmentMin: 200, // enrichment_queue jugée "saturée" au-delà
  famineBacklogMin: 20, // backlog entreprise+école jugé bloqué au-delà
  anonymousPct: 90, // flotte considérée "~100% anonyme" au-delà
  companyJobsBacklogMin: 1000, // company_jobs (priorité la plus basse) jugé non drainé au-delà
};

// Comparaison de versions plugin façon semver (1.1.130 > 1.1.86).
function compareVersions(a: string, b: string): number {
  const pa = a.split(".").map((n) => parseInt(n, 10) || 0);
  const pb = b.split(".").map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d > 0 ? 1 : -1;
  }
  return 0;
}

// Dérive les pathologies de la flotte/des files à partir des données déjà
// chargées (zéro backend). Réévalué à chaque cycle de polling.
export function computeAlerts(
  summary: QueueSummaryResponse | null,
  plugins: PluginStatus[] | null,
): WorkerAlert[] {
  const alerts: WorkerAlert[] = [];

  if (summary) {
    const enrich = (summary.enrichment as QueueSummarySection | undefined)?.count ?? 0;
    const backlog =
      ((summary.company_enrichment as QueueSummarySection | undefined)?.count ?? 0) +
      ((summary.school_enrichment as QueueSummarySection | undefined)?.count ?? 0);
    if (
      enrich > ALERT_THRESHOLDS.famineEnrichmentMin &&
      backlog > ALERT_THRESHOLDS.famineBacklogMin
    ) {
      alerts.push({
        id: "famine",
        severity: "warning",
        title: "Famine probable des files entreprise/école",
        detail: `${backlog} tâches entreprise/école en attente derrière ${enrich} candidats (priorité stricte).`,
        tab: "overview",
      });
    }

    // company_jobs passe en dernier (décision produit) : un backlog modéré est
    // normal, mais une accumulation massive signale que le drain opportuniste ne
    // suffit pas (file scrape supérieure jamais vide, ou flotte pas encore sur le
    // build qui draine l'anonyme).
    const companyJobs = (summary.company_jobs as QueueSummarySection | undefined)?.count ?? 0;
    if (companyJobs > ALERT_THRESHOLDS.companyJobsBacklogMin) {
      alerts.push({
        id: "company_jobs_backlog",
        severity: "warning",
        title: "Backlog d'offres d'emploi non drainé",
        detail: `${companyJobs} tâches company_jobs en attente. Cette file passe en dernier (après candidats/entreprise/école) : elle ne se draine que quand le pool scrape se vide.`,
        tab: "overview",
      });
    }
  }

  if (plugins && plugins.length > 0) {
    const total = plugins.length;
    const anon = plugins.filter((p) => p.is_anonymous).length;
    const anonPct = Math.round((anon / total) * 100);
    if (anonPct >= ALERT_THRESHOLDS.anonymousPct) {
      alerts.push({
        id: "anonymous",
        severity: "warning",
        title: `Flotte ${anonPct}% anonyme`,
        detail:
          "Les files réservées aux comptes connectés (re-scrape, campagnes) ne sont pas drainées.",
        tab: "distribution",
      });
    }

    // The backoffice's verdict: a Bearer sent and refused. A poll without any
    // JWT (heartbeat authenticated, content script anonymous) is not counted.
    const jwtRejected = plugins.filter(isJwtRejected).length;
    if (jwtRejected > 0) {
      alerts.push({
        id: "jwt",
        severity: "danger",
        title: `${jwtRejected} plugin(s) en JWT rejeté`,
        detail:
          "JWT envoyé mais refusé (expiré, invalide) ou invérifiable → le worker ne reçoit plus ses jobs authentifiés (invitations, messages) tant qu'un JWT valide n'arrive pas.",
        tab: "distribution",
      });
    }

    const withErrors = plugins.filter((p) => p.last_worker_error).length;
    if (withErrors > 0) {
      alerts.push({
        id: "errors",
        severity: "danger",
        title: `${withErrors} plugin(s) en erreur`,
        detail: "Une erreur worker a été remontée par heartbeat (détail sur la page Plugins).",
        tab: "distribution",
      });
    }

    const checkpoint = plugins.filter((p) => p.linkedin_checkpoint?.active).length;
    if (checkpoint > 0) {
      alerts.push({
        id: "checkpoint",
        severity: "warning",
        title: `${checkpoint} plugin(s) en pause vérification LinkedIn`,
        detail:
          "Interstitiel de sécurité LinkedIn (checkpoint/login) — l'automatisation reprend après résolution.",
        tab: "distribution",
      });
    }

    const versions = plugins.map((p) => p.plugin_version).filter(Boolean) as string[];
    if (versions.length > 1) {
      const latest = versions.reduce((m, v) => (compareVersions(v, m) > 0 ? v : m), versions[0]);
      const stale = plugins.filter(
        (p) => p.plugin_version && compareVersions(p.plugin_version, latest) < 0,
      ).length;
      if (stale > 0) {
        alerts.push({
          id: "version",
          severity: "warning",
          title: `${stale} plugin(s) sur version obsolète`,
          detail: `Version la plus récente détectée : v${latest}. La propagation Web Store peut prendre quelques jours.`,
          tab: "distribution",
        });
      }
    }
  }

  return alerts;
}
